/**
 * ThemeContext.tsx — UI5 theme state management.
 *
 * Tracks the active SAP-UI5 theme name and persists it to localStorage.
 * Does NOT call ui5SetTheme directly — that is done by PrinceThemeBridge in
 * main.tsx so that both prince-ui and UI5 are updated in one place.
 */

import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

export interface ThemeOption {
  themeName: string;
  displayName: string;
}

// prince-ui Theme-Modell: "light" | "dark" | "cu-light" | "cu-dark" (CONUTI-CI
// hell/dunkel). Wird über prince setTheme() auf <html data-theme> angewandt
// (PrinceThemeBridge in main.tsx). Das frühere "cu" ist in prince-ui nur noch
// ein Alias auf "cu-dark" und wird hier nicht mehr angeboten.
export const THEMES: ThemeOption[] = [
  { themeName: "light", displayName: "Hell" },
  { themeName: "dark", displayName: "Dunkel" },
  { themeName: "cu-light", displayName: "CU Hell" },
  { themeName: "cu-dark", displayName: "CU Dunkel" },
];

const STORAGE_KEY = "maco-status-theme";
const DEFAULT_THEME = "cu-dark";
const VALID = new Set(THEMES.map((t) => t.themeName));

interface ThemeContextValue {
  theme: string;
  setTheme: (theme: string) => void;
  themes: ThemeOption[];
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<string>(() => {
    if (typeof window === "undefined") return DEFAULT_THEME;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    // Alte SAP-Theme-Namen (sap_horizon …) auf Default migrieren.
    return stored && VALID.has(stored) ? stored : DEFAULT_THEME;
  });

  useEffect(() => {
    // Persist the theme choice; actual application (ui5SetTheme + data-ui5-theme
    // + prince setTheme) is handled by PrinceThemeBridge in main.tsx so both
    // systems stay in sync with a single source of truth.
    window.localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: setThemeState, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useAppTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useAppTheme must be used within ThemeProvider");
  return ctx;
}
